import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShieldCheck, Mail, Check, ArrowRight, CreditCard } from "lucide-react";
import { useFamilleConnectee } from "../lib/familleConnectee";
import { CLE_JETON } from "../espace/api";
import { formatAvs, erreurAvs, AVS_PLACEHOLDER } from "../lib/avs";
import { EmailSuggestion } from "../components/EmailSuggestion";
import { PageDecor } from "../components/decor/PageDecor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// La session de l'espace famille, si la famille est connectée : elle prouve déjà l'adresse.
const enteteSession = () => {
  let jeton = "";
  try { jeton = localStorage.getItem(CLE_JETON) || ""; } catch { /* navigation privée */ }
  return jeton ? { Authorization: `Bearer ${jeton}` } : {};
};

// Encart « où trouver le numéro » : la question la plus posée. Répétée ici parce qu'un parent
// qui arrive sur cette page n'a pas forcément le formulaire d'inscription sous les yeux.
const OuTrouver = () => (
  <div
    className="rounded-2xl p-5 mt-6 flex gap-4 items-start"
    style={{ background: "var(--primary-tint)", border: "1px solid rgba(20,184,196,0.25)" }}
    data-testid="avs-ou-trouver"
  >
    <CreditCard size={22} style={{ color: "var(--primary)" }} className="shrink-0 mt-0.5" />
    <div>
      <div className="font-semibold text-sm">Où trouver le numéro AVS&nbsp;?</div>
      <p className="text-sm text-[var(--muted)] mt-1.5 leading-relaxed">
        C'est le numéro à <strong>13 chiffres commençant par 756</strong>, au format
        756.XXXX.XXXX.XX. Il figure sur&nbsp;:
      </p>
      <ul className="text-sm text-[var(--muted)] mt-2 space-y-1 list-disc pl-5">
        <li>la <strong>carte d'assurance-maladie</strong> de l'élève (recto, sous le nom)</li>
        <li>le <strong>certificat de salaire</strong> ou la <strong>déclaration d'impôt</strong></li>
        <li>l'<strong>attestation d'assurance</strong> envoyée chaque année par la caisse</li>
      </ul>
      <p className="text-xs text-[var(--muted)] mt-2.5">
        Chaque personne a son propre numéro : celui de l'enfant, pas celui du parent.
      </p>
    </div>
  </div>
);

export default function MonAvs() {
  // Le lien personnel reçu par e-mail ouvre la page avec « ?jeton=… » : c'est lui qui
  // prouve que l'adresse est bien celle de la famille.
  const [params] = useSearchParams();
  const jeton = params.get("jeton") || "";
  const famille = useFamilleConnectee();
  const [email, setEmail] = useState("");
  const [eleves, setEleves] = useState(null);
  const [saisies, setSaisies] = useState({});
  const [etat, setEtat] = useState("");
  const [erreur, setErreur] = useState("");
  const [lienEnvoye, setLienEnvoye] = useState(false);
  const [fini, setFini] = useState(null);
  const [secours, setSecours] = useState({ prenom: "", nom: "", avs: "", envoye: false });

  // Les élèves de l'adresse prouvée (lien e-mail ou session de l'espace famille).
  const charger = async () => {
    setEtat("recherche");
    setErreur("");
    setEleves(null);
    try {
      const r = await fetch(`${API}/avs/eleves?jeton=${encodeURIComponent(jeton)}`, {
        headers: enteteSession(),
        credentials: "include",
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "");
      if (d.email) setEmail(d.email);
      setEleves(d.eleves || []);
      setEtat("");
    } catch (e) {
      setEtat("");
      setErreur(e.message || "Une erreur est survenue. Réessaie dans un instant.");
    }
  };

  useEffect(() => {
    if (jeton || famille?.email) charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jeton, famille]);

  const demanderLien = async (e) => {
    e.preventDefault();
    setEtat("recherche");
    setErreur("");
    try {
      const r = await fetch(`${API}/avs/lien`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, origine: window.location.origin }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "");
      setLienEnvoye(true);
      setEtat("");
    } catch (err) {
      setEtat("");
      setErreur(err.message || "Une erreur est survenue. Réessaie dans un instant.");
    }
  };

  const aRemplir = (eleves || []).filter((el) => !el.deja_rempli);
  const pret = aRemplir.length > 0 && aRemplir.every((el) => saisies[el.cle] && !erreurAvs(saisies[el.cle]));

  const envoyer = async () => {
    setEtat("envoi");
    try {
      const r = await fetch(`${API}/avs`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...enteteSession() },
        credentials: "include",
        body: JSON.stringify({
          jeton,
          entries: aRemplir.map((el) => ({ cle: el.cle, avs: saisies[el.cle] })),
        }),
      });
      if (!r.ok) throw new Error();
      setFini(await r.json());
      setEtat("");
    } catch {
      setEtat("erreur");
    }
  };

  const envoyerSecours = async () => {
    await fetch(`${API}/avs/aide`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, ...secours }),
    });
    setSecours({ ...secours, envoye: true });
  };

  return (
    <div className="pt-28 pb-24 relative overflow-hidden" data-testid="page-mon-avs">
      <PageDecor />
      <div className="max-w-2xl mx-auto px-6 relative">
        <div className="eyebrow mb-3">Jeunesse+Sport &amp; assurance</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[0.95]">
          Transmets ton<br /><span style={{ color: "var(--primary)" }}>numéro AVS</span>
        </h1>

        <div
          className="rounded-3xl p-6 mt-8 flex gap-5 items-start"
          style={{ background: "#fff", border: "1.5px solid rgba(15,27,45,0.10)" }}
          data-testid="avs-bloc-js"
        >
          <img
            src="/img/jeunesse-et-sport.png"
            alt="Logo Jeunesse+Sport — Confédération suisse"
            className="w-16 h-16 object-contain shrink-0"
            loading="lazy"
          />
          <div>
            <div className="font-display text-xl tracking-tight">
              Complète le dossier J+S de ton enfant
            </div>
            <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">
              1000 ARTS intègre désormais le programme <strong>Jeunesse+Sport (J+S)</strong> de la
              Confédération : des coachs formés et un encadrement de qualité pour nos jeunes
              danseurs. Pour finaliser le dossier de ton enfant, il nous manque une seule chose —
              son <strong>numéro AVS</strong>.
            </p>
            <p className="text-xs text-[var(--muted)] mt-2.5">
              Le dossier J+S concerne les élèves de <strong>2 à 20 ans</strong>.
            </p>
          </div>
        </div>

        <p className="text-base text-[var(--muted)] mt-6 leading-relaxed">
          Le numéro AVS sert aussi pour l'<strong>assurance complémentaire</strong> : beaucoup de
          caisses remboursent une partie des cours de danse, à condition que le numéro de l'élève
          figure sur l'attestation.
        </p>

        {!fini && !eleves && lienEnvoye && (
          <div className="card-flat p-6 mt-8" data-testid="avs-lien-envoye">
            <Mail size={24} style={{ color: "var(--primary)" }} />
            <div className="font-display text-2xl tracking-tight mt-3">Regarde ta boîte e-mail</div>
            <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">
              Si <strong>{email}</strong> est l'adresse d'une famille de l'école, on t'a envoyé un
              lien personnel (valable 2 heures). Ouvre-le pour transmettre le numéro AVS. Pense à
              regarder les indésirables.
            </p>
            <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-sm">
              <button type="button" onClick={() => setLienEnvoye(false)} className="underline font-semibold"
                      data-testid="avs-autre-adresse">
                Essayer une autre adresse
              </button>
              <button type="button" onClick={() => setEleves([])} className="underline font-semibold"
                      data-testid="avs-rien-recu">
                Je n'ai rien reçu
              </button>
            </div>
          </div>
        )}

        {!fini && !eleves && !lienEnvoye && !(jeton && etat === "recherche") && (
          <form onSubmit={demanderLien} className="card-flat p-6 mt-8" data-testid="avs-form-email">
            <label className="field-label">Ton adresse e-mail (celle de l'inscription) *</label>
            <input
              data-testid="avs-email"
              type="email"
              required
              className="input-field"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="prenom.nom@exemple.ch"
            />
            <EmailSuggestion value={email} onFix={setEmail} testid="avs-email-suggestion" />
            <button
              type="submit"
              data-testid="avs-search"
              className="btn-primary mt-4 inline-flex items-center gap-2"
              disabled={etat === "recherche"}
            >
              <Mail size={16} /> {etat === "recherche" ? "Envoi…" : "Recevoir mon lien"}
            </button>
            <p className="text-xs text-[var(--muted)] mt-3 leading-relaxed">
              Pour protéger les données de ta famille, nous t'envoyons un lien personnel par
              e-mail : c'est lui qui ouvre la liste de tes enfants.
            </p>
            {erreur && (
              <p className="text-sm mt-3 font-medium" style={{ color: "#B91C1C" }} data-testid="avs-erreur">
                {erreur}
              </p>
            )}
          </form>
        )}

        {eleves && eleves.length > 0 && !fini && (
          <div className="card-flat p-6 mt-6" data-testid="avs-eleves">
            <div className="font-display text-2xl tracking-tight">
              {eleves.length === 1 ? "1 élève trouvé" : `${eleves.length} élèves trouvés`}
            </div>
            <OuTrouver />

            {eleves.map((el) => (
              <div key={el.cle} className="mt-5 pt-5 border-t border-[var(--border)]">
                <div className="font-semibold flex items-center gap-2 flex-wrap">
                  {el.prenom} {el.nom}
                  {el.js && (
                    <span
                      className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                      style={{ background: "var(--primary-tint)", color: "#0E7A85" }}
                      data-testid={`avs-js-${el.cle}`}
                    >
                      Dossier J+S
                    </span>
                  )}
                </div>
                {el.deja_rempli ? (
                  <p className="text-sm mt-1 font-medium flex items-center gap-1.5" style={{ color: "#15803D" }} data-testid={`avs-deja-${el.cle}`}>
                    <Check size={15} /> Numéro déjà enregistré — merci&nbsp;! Pour le corriger, écris-nous.
                  </p>
                ) : (
                  <>
                    <input
                      data-testid={`avs-input-${el.cle}`}
                      className="input-field mt-2"
                      inputMode="numeric"
                      value={saisies[el.cle] || ""}
                      onChange={(e) => setSaisies({ ...saisies, [el.cle]: formatAvs(e.target.value) })}
                      placeholder={AVS_PLACEHOLDER}
                    />
                    {erreurAvs(saisies[el.cle]) ? (
                      <p className="text-xs mt-1 font-medium" style={{ color: "#B91C1C" }} data-testid={`avs-error-${el.cle}`}>
                        {erreurAvs(saisies[el.cle])}
                      </p>
                    ) : saisies[el.cle] ? (
                      <p className="text-xs mt-1 font-medium" style={{ color: "#15803D" }}>Numéro valide ✓</p>
                    ) : null}
                  </>
                )}
              </div>
            ))}

            {aRemplir.length > 0 && (
              <button
                type="button"
                data-testid="avs-submit"
                onClick={envoyer}
                disabled={!pret || etat === "envoi"}
                className="btn-primary mt-6 w-full inline-flex items-center justify-center gap-2"
                style={{ opacity: pret ? 1 : 0.5 }}
              >
                {etat === "envoi" ? "Enregistrement…" : "Enregistrer"} <ArrowRight size={16} />
              </button>
            )}

            <p className="text-xs text-[var(--muted)] mt-5 leading-relaxed" data-testid="avs-enfant-manquant">
              Un enfant manque dans cette liste&nbsp;? C'est qu'il n'est pas encore inscrit :
              passe par le <Link to="/inscription" className="underline hover:text-[var(--primary)]">formulaire d'inscription</Link>,
              ou écris-nous à <a href="mailto:info1000arts@gmail.com" className="underline hover:text-[var(--primary)]">info1000arts@gmail.com</a>.
            </p>
          </div>
        )}

        {eleves && eleves.length === 0 && !fini && (
          <div className="card-flat p-6 mt-6" data-testid="avs-introuvable">
            <div className="font-display text-2xl tracking-tight">
              {lienEnvoye ? "Tu n'as pas reçu de lien ?" : "Aucune inscription trouvée"}
            </div>
            <p className="text-sm text-[var(--muted)] mt-2">
              {lienEnvoye
                ? "Cette adresse n'est peut-être pas celle de l'inscription. Indique le prénom et le nom de l'élève, et l'école s'en occupe à la main."
                : "Cette adresse ne correspond à aucune inscription en cours — peut-être en as-tu utilisé une autre. Indique le prénom et le nom de l'élève, et l'école s'en occupe à la main."}
            </p>
            <OuTrouver />
            {secours.envoye ? (
              <p className="text-sm mt-4 font-medium" style={{ color: "#15803D" }} data-testid="avs-aide-envoye">
                Merci, l'école a reçu ta demande et te recontactera si besoin.
              </p>
            ) : (
              <>
                <div className="grid md:grid-cols-2 gap-3 mt-4">
                  <input data-testid="avs-aide-prenom" className="input-field" placeholder="Prénom de l'élève" value={secours.prenom} onChange={(e) => setSecours({ ...secours, prenom: e.target.value })} />
                  <input data-testid="avs-aide-nom" className="input-field" placeholder="Nom de l'élève" value={secours.nom} onChange={(e) => setSecours({ ...secours, nom: e.target.value })} />
                </div>
                <input data-testid="avs-aide-avs" className="input-field mt-3" inputMode="numeric" placeholder={AVS_PLACEHOLDER} value={secours.avs} onChange={(e) => setSecours({ ...secours, avs: formatAvs(e.target.value) })} />
                {erreurAvs(secours.avs) && <p className="text-xs mt-1 font-medium" style={{ color: "#B91C1C" }}>{erreurAvs(secours.avs)}</p>}
                <button
                  type="button"
                  data-testid="avs-aide-submit"
                  onClick={envoyerSecours}
                  disabled={!secours.prenom || !secours.nom || !secours.avs || !!erreurAvs(secours.avs)}
                  className="btn-primary mt-4"
                  style={{ opacity: secours.prenom && secours.nom && secours.avs && !erreurAvs(secours.avs) ? 1 : 0.5 }}
                >
                  Envoyer à l'école
                </button>
              </>
            )}
          </div>
        )}

        {fini && (
          <div className="card-flat p-8 mt-8 text-center" data-testid="avs-merci">
            <ShieldCheck size={40} className="mx-auto" style={{ color: "var(--primary)" }} />
            <div className="font-display text-3xl tracking-tight mt-4">Merci&nbsp;!</div>
            <p className="text-sm text-[var(--muted)] mt-3">
              {fini.resultats.map((r) => r.eleve).join(", ")} — c'est enregistré. Tu n'as plus rien
              à faire, l'école utilisera ce numéro pour tes attestations.
            </p>
            <Link to="/" className="btn-ghost mt-6 inline-flex">Retour à l'accueil</Link>
          </div>
        )}

        {etat === "erreur" && (
          <p className="text-sm mt-4 font-medium" style={{ color: "#B91C1C" }} data-testid="avs-etat-erreur">
            Une erreur est survenue. Réessaie dans un instant ou écris-nous à info1000arts@gmail.com.
          </p>
        )}
      </div>
    </div>
  );
}
